/**
 * BrowserNotificationPrompt - Prompt user to enable browser notifications
 * Shows a dismissible banner with permission request
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { clsx } from 'clsx';
import { Bell, X, CheckCircle } from 'lucide-react';
import { useBrowserNotifications } from '@hooks/useBrowserNotifications';
import { useAuthStore } from '@stores/authStore';

const DISMISSED_KEY = 'neokanban_browser_notifications_dismissed';
const PROMPT_DELAY = 5000; // Show prompt after 5 seconds

export default function BrowserNotificationPrompt() {
  const { t } = useTranslation();
  // Permission priming belongs in-app: anonymous visitors must never see it
  const accessToken = useAuthStore((s) => s.accessToken);
  const { isSupported, isEnabled, permissionStatus, requestPermission, showTestNotification } =
    useBrowserNotifications();
  const [isDismissed, setIsDismissed] = useState(true);
  const [isRequesting, setIsRequesting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    // Don't show if not supported or already enabled
    if (!isSupported || isEnabled) {
      return;
    }

    // Don't show if previously dismissed
    const dismissed = localStorage.getItem(DISMISSED_KEY);
    if (dismissed === 'true') {
      return;
    }

    // Show prompt after delay
    const timer = setTimeout(() => {
      setIsDismissed(false);
    }, PROMPT_DELAY);

    return () => clearTimeout(timer);
  }, [isSupported, isEnabled]);

  const handleEnable = async () => {
    setIsRequesting(true);
    try {
      const status = await requestPermission();
      if (status === 'granted') {
        // Show success message
        setShowSuccess(true);
        // Show test notification
        await showTestNotification();
        // Auto-dismiss after 3 seconds
        setTimeout(() => {
          setIsDismissed(true);
        }, 3000);
      } else {
        // User denied or dismissed, don't show again
        setIsDismissed(true);
        localStorage.setItem(DISMISSED_KEY, 'true');
      }
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      setIsDismissed(true);
    } finally {
      setIsRequesting(false);
    }
  };

  const handleDismiss = () => {
    setIsDismissed(true);
    localStorage.setItem(DISMISSED_KEY, 'true');
  };

  // Don't render if dismissed or not supported or already enabled
  if (!accessToken || isDismissed || !isSupported || isEnabled || permissionStatus === 'denied') {
    return null;
  }

  return (
    <div className="fixed bottom-20 lg:bottom-4 right-4 z-50 max-w-md animate-in slide-in-from-bottom-5 duration-300">
      <div
        className={clsx(
          'rounded-lg border shadow-lg',
          showSuccess
            ? 'border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950'
            : 'border-neutral-200 bg-white dark:border-neutral-700 dark:bg-neutral-800'
        )}
      >
        <div className="p-4">
          {/* Header */}
          <div className="flex items-start gap-3">
            <div
              className={clsx(
                'flex h-10 w-10 shrink-0 items-center justify-center rounded-full',
                showSuccess
                  ? 'bg-green-100 text-green-600 dark:bg-green-900 dark:text-green-400'
                  : 'bg-blue-100 text-blue-600 dark:bg-blue-900 dark:text-blue-400'
              )}
            >
              {showSuccess ? <CheckCircle className="h-5 w-5" /> : <Bell className="h-5 w-5" />}
            </div>

            <div className="flex-1">
              <h3
                className={clsx(
                  'text-sm font-semibold',
                  showSuccess
                    ? 'text-green-900 dark:text-green-100'
                    : 'text-neutral-900 dark:text-white'
                )}
              >
                {showSuccess ? t('notifications.notificationsEnabled') : t('notifications.enableBrowserNotifications')}
              </h3>
              <p
                className={clsx(
                  'mt-1 text-sm',
                  showSuccess
                    ? 'text-green-700 dark:text-green-300'
                    : 'text-neutral-600 dark:text-neutral-400'
                )}
              >
                {showSuccess
                  ? t('notifications.notificationsEnabledDescription')
                  : t('notifications.browserNotificationsDescription')}
              </p>
            </div>

            {!showSuccess && (
              <button
                onClick={handleDismiss}
                className="shrink-0 text-neutral-400 hover:text-neutral-600 dark:text-neutral-500 dark:hover:text-neutral-300"
                aria-label={t('common.dismiss')}
              >
                <X className="h-5 w-5" />
              </button>
            )}
          </div>

          {/* Actions */}
          {!showSuccess && (
            <div className="mt-4 flex gap-2">
              <button
                onClick={handleEnable}
                disabled={isRequesting}
                className={clsx(
                  'flex-1 rounded-md px-4 py-2 text-sm font-medium transition-colors',
                  'bg-blue-600 text-white hover:bg-blue-700',
                  'dark:bg-blue-500 dark:hover:bg-blue-600',
                  'disabled:cursor-not-allowed disabled:opacity-50'
                )}
              >
                {isRequesting ? 'Enabling...' : t('notifications.enable')}
              </button>
              <button
                onClick={handleDismiss}
                className={clsx(
                  'rounded-md px-4 py-2 text-sm font-medium transition-colors',
                  'text-neutral-700 hover:bg-neutral-100',
                  'dark:text-neutral-300 dark:hover:bg-neutral-700'
                )}
              >
                {t('notifications.notNow')}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
